import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Menu,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';

type RangeFilterVariant = 'currency' | 'percent' | 'integer';

export type NumericRange = {
  min?: number;
  max?: number;
};

type TableFilterRangeDropdownProps = {
  label?: string;
  value: NumericRange;
  onChange: (next: NumericRange) => void;
  variant?: RangeFilterVariant;
};

const linkButtonSx = {
  padding: 0,
  minWidth: 'auto',
  fontSize: '12px',
  fontWeight: 500,
  color: '#51B8B0',
  textTransform: 'none' as const,
  '&:hover': {
    backgroundColor: 'transparent',
    textDecoration: 'underline',
  },
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#F0F0F1',
    fontSize: '0.875rem',
    '& fieldset': { border: 'none' },
    '&:hover fieldset': { border: 'none' },
    '&.Mui-focused fieldset': { border: 'none' },
  },
} as const;

const parseOptionalNumber = (raw: string): number | undefined => {
  const t = raw.trim();
  if (!t) return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
};

const sanitizeRangeFilterInput = (raw: string, filterVariant: RangeFilterVariant): string => {
  const allowDecimalSeparator = filterVariant !== 'integer';
  let result = '';
  let hasDecimalSeparator = false;

  for (const character of raw) {
    if (character === '-' && result.length === 0) {
      result += character;
      continue;
    }
    if (character >= '0' && character <= '9') {
      result += character;
      continue;
    }
    if (allowDecimalSeparator && character === '.' && !hasDecimalSeparator) {
      hasDecimalSeparator = true;
      result += character;
    }
  }
  return result;
};

const TableFilterRangeDropdown: FC<TableFilterRangeDropdownProps> = ({
  label,
  value,
  onChange,
  variant = 'integer',
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [minStr, setMinStr] = useState('');
  const [maxStr, setMaxStr] = useState('');

  const isOpen = Boolean(anchorEl);
  const menuId = `range-filter-${(label ?? 'metric').replace(/\s+/g, '-')}`;

  const isActive = value.min !== undefined || value.max !== undefined;

  useEffect(() => {
    if (!isOpen) return;
    setMinStr(value.min !== undefined ? String(value.min) : '');
    setMaxStr(value.max !== undefined ? String(value.max) : '');
  }, [isOpen, value.min, value.max]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    const next: NumericRange = {};
    const min = parseOptionalNumber(minStr);
    const max = parseOptionalNumber(maxStr);
    if (min !== undefined) next.min = min;
    if (max !== undefined) next.max = max;

    if (value.min !== next.min || value.max !== next.max) {
      onChange(next);
    }
    setAnchorEl(null);
  };

  const handleReset = () => {
    setMinStr('');
    setMaxStr('');
    onChange({});
  };

  const startAdornment =
    variant === 'currency' ? <InputAdornment position="start">$</InputAdornment> : undefined;
  const endAdornment =
    variant === 'percent' ? <InputAdornment position="end">%</InputAdornment> : undefined;

  return (
    <>
      <Box
        sx={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
        }}
      >
        {isActive && (
          <Box
            sx={{
              width: '1.25rem',
              height: '1.25rem',
              bgcolor: '#FF6347',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: '#fff',
                lineHeight: 1,
                fontWeight: 700,
              }}
            >
              1
            </Typography>
          </Box>
        )}
        <IconButton
          size="small"
          onClick={handleOpen}
          aria-label={`${label ?? 'Metric'} range filter`}
          aria-controls={isOpen ? menuId : undefined}
          aria-haspopup="true"
          aria-expanded={isOpen ? 'true' : undefined}
          color={isActive ? 'primary' : 'default'}
        >
          <FilterAltOutlinedIcon
            sx={{
              fontSize: '1.25rem',
              cursor: 'pointer',
              color: '#162C36',
            }}
          />
        </IconButton>
      </Box>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        MenuListProps={{
          sx: {
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            p: 0,
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: '0.5rem',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minWidth: 200,
            maxWidth: 280,
          },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          px="8px"
          py="6px"
          sx={{
            borderBottom: '1px solid #737F86',
            flexShrink: 0,
          }}
          gap={1}
        >
          <Typography
            fontSize="11px"
            fontWeight={700}
            color="#6C6C6C"
            sx={{ mr: 0.5, lineHeight: 1.2 }}
          >
            {label ?? 'Filter'}
          </Typography>
          <Button onClick={handleReset} sx={linkButtonSx}>
            Reset
          </Button>
        </Stack>
        <Box sx={{ px: '8px', py: '8px' }}>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <TextField
              size="small"
              value={minStr}
              onChange={(e) => setMinStr(sanitizeRangeFilterInput(e.target.value, variant))}
              placeholder="Min"
              type="text"
              inputMode={variant === 'integer' ? 'numeric' : 'decimal'}
              sx={{
                ...inputSx,
                flex: 1,
                minWidth: 0,
                '& .MuiInputBase-input': { py: '6px', px: '6px' },
              }}
              InputProps={{
                startAdornment,
                endAdornment,
              }}
            />
            <Typography
              fontSize="0.75rem"
              fontWeight={500}
              color="#162936"
              sx={{ flexShrink: 0, px: 0.25 }}
            >
              to
            </Typography>
            <TextField
              size="small"
              value={maxStr}
              onChange={(e) => setMaxStr(sanitizeRangeFilterInput(e.target.value, variant))}
              placeholder="Max"
              type="text"
              inputMode={variant === 'integer' ? 'numeric' : 'decimal'}
              sx={{
                ...inputSx,
                flex: 1,
                minWidth: 0,
                '& .MuiInputBase-input': { py: '6px', px: '6px' },
              }}
              InputProps={{
                startAdornment,
                endAdornment,
              }}
            />
          </Stack>
        </Box>
      </Menu>
    </>
  );
};

export default TableFilterRangeDropdown;
